import type { ComponentType } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import { Redirect } from '../../router/react-router-v5-compat';
import i18n from '../../i18n';
import RestaurantBasisInfoPage from './restaurant-basic-info.page';
import DinnerTablePage from './dinner-table.page';
import CustomerPage from './customer.page';
import ResrveMealPeriodPage from './reserve-meal-period.page';
import TableMarkers from './table-markers.page';
import './scss/my-restaurant.page.scss';
import { HookDecorator } from '../main/main-page-states';

/** 「我的餐厅」路由前缀，子 Tab 均挂在此路径下 */
const MY_RESTAURANT_BASE = '/main/rbp-cloud/my-restaurant';

/**
 * 顶部 Tab 与业务子页的对应关系（「营业时间」已合并进餐厅基本信息页，不再单独 Tab）
 */
const TAB_DEFS: {
    /** 完整 hash 路径 */
    path: string;
    /** 侧栏与 Tab 标题 i18n key */
    i18nKey: string;
    /** 子页组件 */
    Component: ComponentType;
}[] = [
        { path: `${MY_RESTAURANT_BASE}/info`, i18nKey: 'restaurant_basic_info', Component: RestaurantBasisInfoPage },
        { path: `${MY_RESTAURANT_BASE}/table-area`, i18nKey: 'dinner_table_page_label', Component: DinnerTablePage },
        { path: `${MY_RESTAURANT_BASE}/customers`, i18nKey: 'customers_page_info', Component: CustomerPage },
        { path: `${MY_RESTAURANT_BASE}/reservations`, i18nKey: 'reserve_meal_page_setting', Component: ResrveMealPeriodPage },
        { path: `${MY_RESTAURANT_BASE}/table-markers`, i18nKey: 'table_markers', Component: TableMarkers },
    ];

const DEFAULT_TAB_PATH = TAB_DEFS[0].path;

/**
 * 云 RBP「我的餐厅」壳页面：顶部 Tab 切换各子设置页（对齐设计稿 #7468f0 激活态与 56px 高度）
 */
export default HookDecorator(() => {
    const history = useHistory();
    const { pathname } = useLocation();

    /** 落在前缀根路径时进入默认 Tab */
    if (pathname === MY_RESTAURANT_BASE || pathname === `${MY_RESTAURANT_BASE}/`) {
        return <Redirect to={DEFAULT_TAB_PATH} />;
    }
    const active = TAB_DEFS.find((t) => t.path === pathname);
    if (!active) {
        return <Redirect to={DEFAULT_TAB_PATH} />;
    }

    const { Component } = active;

    return (
        <Box className="my-restaurant-page">
            <Tabs
                className="my-restaurant-page__tabs"
                value={pathname}
                onChange={(_, value: string) => history.push(value)}
                variant="scrollable"
                scrollButtons="auto"
                TabIndicatorProps={{ style: { display: 'none' } }}
            >
                {TAB_DEFS.map((t) => (
                    <Tab
                        key={t.path}
                        value={t.path}
                        disableRipple
                        label={i18n.t(t.i18nKey)}
                        className="my-restaurant-page__tab"
                    />
                ))}
            </Tabs>
            <Box className="my-restaurant-page__panel">
                <Component />
            </Box>
        </Box>
    );
});
